/// <amd-module name="Wit/Deployment/Components/PipelineCard" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { ago } from "VSSUI/Utilities/Date";
import { IPipeline, IStage } from "Wit/Deployment/Contracts";
import * as Resources from "Wit/Deployment/Resources";
import { DeploymentsIntegrationCIActions, publishDeploymentsTelemetry } from "Wit/Deployment/telemetry/Deployments";

interface IPipelineCardProps {
    pipeline: IPipeline;
}

interface IPipelineCardState {
    isExpanded: boolean;
}

export class PipelineCard extends VssComponent<IPipelineCardProps, IPipelineCardState> {
    public constructor(props: IPipelineCardProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isExpanded: false
        };
    }

    public render(): JSX.Element {
        const { pipeline } = this.props;
        const { isExpanded } = this.state;
        return (
            <Card className="pipeline-card" contentProps={{ contentPadding: false }}>
                <div className="pipeline-container" onClick={this.toggleContentClick} onKeyDown={this.toggleContentEnter} role="button" tabIndex={0}>
                    <div className="fixed-width">{this.getStatus(pipeline.status)}</div>
                    <div className="content">
                        <div className="header">
                            <Link
                                className="body-m primary-text title"
                                href={pipeline.url}
                                target="_blank"
                                removeUnderline={true}
                            >
                                {pipeline.name} ({pipeline.stages.length})
                            </Link>
                            <div className="body-s secondary-text text-ellipsis">
                                {pipeline.stages.length > 0 && format(Resources.PipelineSecondaryText, ago(pipeline.date), pipeline.stages[0].name)}
                            </div>
                        </div>
                        <div className={css("stages", !isExpanded && "collapsed")}>{this.renderStages(pipeline.stages)}</div>
                    </div>
                    <div className="fixed-width">
                        <Icon ariaLabel="Chevron icon" iconName="ChevronUp" className={css("chevron", !isExpanded && "collapsed")} />
                    </div>
                </div>
            </Card>
        );
    }

    private getToolTipText(stage: IStage): string {
        return `${this.localizedStatus(stage.status)} ${ago(stage.date)}`;
    }

    private renderStages(stages: IStage[]) {
        return stages.map((stage: IStage) => {
            const key = `${stage.runId}-${stage.name}-${stage.sequenceNumber}`;
            return (
                <Tooltip text={this.getToolTipText(stage)} key={key}>
                    <div className="stage flex" key={key}>
                        {this.getStatus(stage.status)}&nbsp;
                        <span className="body-s primary-text">
                            <Link href={stage.url} target="_blank">
                                #{stage.runId}
                            </Link>
                            <Icon ariaLabel="Forward icon" className="stage-arrow-icon" iconName="Forward" /> {stage.name}
                        </span>
                    </div>
                </Tooltip>
            );
        });
    }

    private localizedStatus(status: string): string {
        switch (status) {
            case "pending":
                return Resources.Pending;
            case "in_progress":
                return Resources.Inprogress;
            case "successful":
                return Resources.Successful;
            case "canceled":
                return Resources.Canceled;
            case "failed":
                return Resources.Failed;
            case "rolled_back":
                return Resources.Rolledback;
            default:
                return Resources.Unknown;
        }
    }

    private getStatus(status: string): JSX.Element {
        const statusPropsDictionary: { [status: string]: IStatusProps } = {
            successful: Statuses.Success,
            failed: Statuses.Failed,
            unknown: Statuses.Failed,
            // Right now we're just reporting the first three.
            // Adding the others for the future.
            pending: Statuses.Waiting,
            in_progress: Statuses.Running,
            canceled: Statuses.Canceled,
            rolled_back: Statuses.Failed
        };

        return <Status {...statusPropsDictionary[status]} key={status} size={StatusSize.m} className="deployment-status" />;
    }

    private toggleContentClick = (event: React.MouseEvent<HTMLElement>): void => {
        const target = event.target as HTMLElement;
        if (target.tagName === "A") {
            return;
        }

        this.toggleContent();
    };

    private toggleContentEnter = (event: React.KeyboardEvent<HTMLElement>): void => {
        const code = event.which;
        if (code === KeyCode.enter) {
            this.toggleContent();
        }
    };

    private toggleContent = (): void => {
        publishDeploymentsTelemetry(this.context.pageContext, DeploymentsIntegrationCIActions.TOGGLE_PIPELINE, {
            isExpanded: !this.state.isExpanded
        });
        this.setState({
            isExpanded: !this.state.isExpanded
        });
    };
}
